import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import ErrorList from 'Components/UI/ErrorList';
import LoadingIndicator from 'Components/UI/LoadingIndicator';

const styles = theme => ({
  root: {},
  rootBackgroundColorWhite: {
    backgroundColor: theme.palette.common.white,
  },
  rootBackgroundColorLightGray: {
    backgroundColor: theme.palette.facet.lightGray,
  },
  rootLayoutVertical: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  rootLoading: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  rootLoadingError: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(4),
  },
  indicatorContainer: {},
  content: {},
});

function DataContainer({
  backgroundColor,
  children,
  className,
  classes,
  component,
  componentProps,
  loadingError,
  layout,
  loading,
  loadingIndicatorColor,
  skeleton,
  LoadingIndicatorProps,
  ErrorListProps,
  SkeletonProps,
  ...rest
}) {
  if (!loading && !loadingError) {
    if (component) {
      const Component = component;
      return (<Component {...componentProps} />);
    }

    return children;
  }

  let loadingComponent;

  if (loading && skeleton) {
    const SkeletonComponent = skeleton;
    loadingComponent = (
      <SkeletonComponent {...SkeletonProps} />
    );
  } else if (loading) {
    loadingComponent = (
      <div className={classes.indicatorContainer} test="data-container-loading-indicator">
        <LoadingIndicator
          color={loadingIndicatorColor}
          {...LoadingIndicatorProps}
        />
      </div>
    );
  }

  return (
    <div
      className={classNames(classes.root, {
        [classes.rootLayoutVertical]: layout === 'vertical',
        [classes.rootLoading]: loading,
        [classes.rootLoadingError]: loadingError,
        [classes.rootBackgroundColorWhite]: backgroundColor === 'white',
        [classes.rootBackgroundColorLightGray]: backgroundColor === 'lightGray',
      }, className)}
      {...rest}
    >
      {loadingComponent}
      {(!loading && loadingError) ? (
        <ErrorList
          title="There was an error loading the page. Please try again."
          errors={loadingError?.errors}
          {...ErrorListProps}
        />
      ) : null}
    </div>
  );
}

DataContainer.defaultProps = {
  backgroundColor: 'none',
  className: '',
  component: null,
  componentProps: null,
  children: null,
  loadingError: undefined,
  layout: 'vertical',
  skeleton: null,
  ErrorListProps: {},
  LoadingIndicatorProps: {},
  SkeletonProps: {},
};

DataContainer.propTypes = {
  backgroundColor: PropTypes.oneOf(['none', 'white', 'lightGray']),
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  component: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.object,
  ]),
  componentProps: PropTypes.object,
  loadingError: PropTypes.object,
  layout: PropTypes.oneOf(['vertical', 'block']), // Use "vertical" to get a vertical flexbox container that takes up as much space as possible, or "block" to get a `display: block` container
  loading: PropTypes.bool.isRequired,
  skeleton: PropTypes.oneOfType([PropTypes.node, PropTypes.element, PropTypes.func, PropTypes.object]),
  ErrorListProps: PropTypes.object,
  LoadingIndicatorProps: PropTypes.object,
  SkeletonProps: PropTypes.object,
};

export default withStyles(styles)(DataContainer);
